import { GraphRequest, RestRequest } from './request'

const SYNC_MS = 60000

export default class RavelloHelper {
	/**
	 * @constructor
	 * @param {object} store - Vuex store
	 * @param {object} service - Ravello service
	 */
	constructor(store, service) {
		this._store = store
		this._service = service
		this._refreshLoop = null
		this._syncLoop = null
	}

	/**
	 * Get base URL
	 */
	static get baseUrl() {
		return window.appConfigs.HUB_URL
	}

	/**
	 * Get SSO URL
	 */
	static get ssoUrl() {
		return window.appConfigs.SSO_URL
	}

	static get ssoClientId() {
		return window.appConfigs.SSO_CLIENT_ID
	}

	/**
	 * Redirect to the SSO login page
	 */
	static ssoRedirectToLogin() {
		// eslint-disable-next-line max-len
		window.location = `${RavelloHelper.ssoUrl}/authorize?client_id=${RavelloHelper.ssoClientId}&redirect_url=${encodeURIComponent(window.location.pathname)}`
	}

	/**
	 * Is authenticated
	 * @return {boolean} - Authentication status
	 */
	isAuth() {
		return this._store.getters['session/isAuth']
	}

	/**
	 * Is loaded
	 * @return {boolean} - Authentication loading status
	 */
	isAuthLoaded() {
		return this._store.getters['session/isLoaded']
	}

	/**
	 * Is current token expired
	 * @return {boolean} - Expiration status
	 */
	isExpired() {
		return this._store.getters['session/isExpired']
	}

	/**
	 * Refresh JWT token
	 * @param {object} payload - Credentials payload
	 */
	async refresh() {
		try {
			const response = await this._service.login({}, 'token')
			this._store.dispatch('session/login', response)
		} catch (err) {
			if (err.status === 400) {
				this.logout(false)
			} else {
				console.warn(err.message)
			}
		}
	}

	async syncFeatureFlags() {
		if (!this.isAuth()) { return }

		try {
			const response = await this.graphql('featureflags')
				.datasource('key', 'active')
				.query()

			const flags = response.reduce((acc, { text, value }) => ({
				...acc,
				[text]: value,
			}), {})
			this._store.dispatch('session/setFeatureFlags', flags)
		} catch (err) {
			console.warn(err.message)
		}
	}

	async initSession() {
		if (!this.isAuth()) { return }

		if (this.isExpired()) {
			await this.ssoRefresh()
		} else {
			await this.identify()
		}
	}

	/**
	 * Identify user
	 */
	async identify() {
		if (!this.isAuth()) { return }

		const jwt = this._store.getters['session/getToken']
		this._service.setBearerToken(jwt)

		await this.syncFeatureFlags()
		const profile = await this._service.ssoRequest('profile', {}, { method: 'get' })

		this._store.dispatch('session/identify', profile)

		let expiration = this._store.getters['session/getExpirationSeconds']

		// Should not happen
		if (expiration <= 0) {
			expiration = 1000
		}

		this._refreshLoop = setTimeout(async () => {
			await this.ssoRefresh()
		}, expiration)

		this._syncLoop = setTimeout(async () => {
			await this.syncFeatureFlags()
		}, SYNC_MS)
	}

	/**
	 * Logout
	 */
	async logout(sendLogout = true) {
		this._store.dispatch('session/logout')
		if (this._refreshLoop) {
			clearTimeout(this._refreshLoop)
			clearTimeout(this._syncLoop)
		}

		if (sendLogout) {
			await this._service.logout()
		}
		this._service.unsetBearerToken()
	}

	/**
	 * Login
	 * @param {object} payload - Credentials payload
	 * @param {string} strategy - Login strategy
	 */
	async login(payload, strategy) {
		const response = await this._service.login(payload, strategy)
		if (response.require_2fa) { return response }
		this._store.dispatch('session/login', response)
		await this.identify()

		return true
	}

	async ssoAuthorize(authorizationCode) {
		if (this._refreshLoop) {
			clearTimeout(this._refreshLoop)
			clearTimeout(this._syncLoop)
		}

		try {
			const response = await this._service.ssoRequest('authorize', {
				authorizationCode,
			})

			this._store.dispatch('session/authenticate', response)
			const jwt = this._store.getters['session/getToken']
			this._service.setBearerToken(jwt)

			await this.identify()
		} catch (err) {
			console.warn(err)
		}
	}

	async ssoRefresh() {
		if (this._refreshLoop) {
			clearTimeout(this._refreshLoop)
			clearTimeout(this._syncLoop)
		}

		try {
			const refreshToken = this._store.getters['session/getRefreshToken']
			const response = await this._service.ssoRequest('refresh', {
				refreshToken,
			})

			this._store.dispatch('session/refresh', response)
			const jwt = this._store.getters['session/getToken']
			this._service.setBearerToken(jwt)

			await this.identify()
		} catch (err) {
			console.warn(err)
		}
	}

	async ssoLogout() {
		if (this._refreshLoop) {
			clearTimeout(this._refreshLoop)
			clearTimeout(this._syncLoop)
		}

		const refreshToken = this._store.getters['session/getRefreshToken']

		try {
			await this._service.ssoRequest('logout', {
				refreshToken,
			})
		} catch (err) {
			console.log(err)
		}

		this._store.dispatch('session/logout')
		this._service.unsetBearerToken()
		RavelloHelper.ssoRedirectToLogin()
	}

	/**
	 * Whether logged user has permission
	 * @param {string} permission - Permission
	 * @return {boolean} - Permission
	 */
	can(permission) {
		return this._store.getters['session/isAuthorized'](permission)
	}

	/**
	 * Wheter feature flag is active
	 * @param {string} flag - Feature flag
	 * @return {boolean} - Feature flag
	 */
	ff(flag) {
		return this._store.getters['session/isFeatureFlagActive'](flag)
	}

	/**
	 * Return namespaced websocket
	 * @param {string} namespace - Namespace
	 */
	socket(namespace = '') {
		if (!this.isAuth()) { return null }

		return this._service.initSocket(namespace)
	}

	/**
	 * Get logged user
	 * @return {object} - User
	 */
	get me() {
		return this._store.getters['session/me']
	}

	/**
	 * Return new Ravello rest request helper
	 * @param {string} type - Request type (default: rest)
	 * @return {object} - Ravello rest request helper
	 */
	request() {
		return new RestRequest(this._service)
	}

	/**
	 * Return new Ravello graph request helper
	 * @param {string} node - Node
	 * @param {string} id - Node entry ID (Optional, useful for single retrieval)
	 * @return {object} - Ravello graph request helper
	 */
	graphql(node, id = null) {
		return new GraphRequest(this._service, node, id)
	}
}
